/**
 * Check steps of an app lab run (spec §7 "Prüfschritte ... echt statt simuliert", #1712).
 *
 * Every step is derived from a signal the system really emits: the agent's
 * events (CampusApp.psm1 / Start-CampusAgent.ps1 - appId carries the recipe id)
 * and the orchestrator state. Spec §7 also names "Script-Syntax" and
 * "Smoke-Test"; the agent emits neither in a lab run (smoke runs only in the
 * image verify phase), so they are not shown rather than faked.
 */

import type { LabEvent } from '../services/appsService';

export type LabStepState = 'pending' | 'running' | 'done' | 'failed';

export interface LabCheckStep {
  label: string;
  state: LabStepState;
}

export const LAB_CHECK_LABELS = [
  'Test-VM aus der aktuellen Basis erstellt',
  'Voraussetzungen installiert',
  'Installation ausgeführt',
  'Erkennungsregel erfüllt',
  'Test-VM gelöscht',
] as const;

/** Orchestrator fail classes that happen before the agent ever reports. */
const INFRA_FAIL = new Set(['vm-create', 'agent-silent']);
const ACTIVE_STATES = new Set(['created', 'vm-starting', 'running']);

interface Input {
  rootId: string;
  prereqIds: string[];
  events: LabEvent[];
  jobState?: string | null;
  failClass?: string | null;
}

const has = (events: LabEvent[], phase: string | string[], status: string, appId?: string): boolean =>
  events.some(
    (e) =>
      (Array.isArray(phase) ? phase.includes(e.phase ?? '') : e.phase === phase) &&
      e.status === status &&
      (appId === undefined || e.appId === appId)
  );

export function deriveLabCheckSteps({ rootId, prereqIds, events, jobState, failClass }: Input): LabCheckStep[] {
  const active = ACTIVE_STATES.has(jobState ?? '');
  const finished = jobState === 'done' || jobState === 'failed' || jobState === 'cleaned';

  const vmUp = has(events, 'bootstrap', 'ok');
  const vm: LabStepState = vmUp
    ? 'done'
    : has(events, 'bootstrap', 'fail') || INFRA_FAIL.has(failClass ?? '')
      ? 'failed'
      : active
        ? 'running'
        : 'pending';

  // An already-installed recipe only reports detect/ok - it is installed all the same.
  const recipeDone = (id: string) => has(events, 'detect', 'ok', id);
  const recipeFailed = (id: string) => has(events, ['prereq', 'download', 'install', 'detect'], 'fail', id);

  const prereqFailed = prereqIds.some(recipeFailed);
  const prereqs: LabStepState = prereqFailed
    ? 'failed'
    : vmUp && prereqIds.every(recipeDone)
      ? 'done'
      : vmUp
        ? 'running'
        : 'pending';

  const rootInstalled = has(events, 'install', 'ok', rootId) || recipeDone(rootId);
  const install: LabStepState = has(events, ['prereq', 'download', 'install'], 'fail', rootId)
    ? 'failed'
    : rootInstalled
      ? 'done'
      : has(events, ['download', 'install'], 'started', rootId)
        ? 'running'
        : 'pending';

  const detect: LabStepState = has(events, 'detect', 'fail', rootId)
    ? 'failed'
    : recipeDone(rootId)
      ? 'done'
      : install === 'done'
        ? 'running'
        : 'pending';

  const cleanup: LabStepState = jobState === 'cleaned' ? 'done' : finished ? 'running' : 'pending';

  return [vm, prereqs, install, detect, cleanup].map((state, i) => ({ label: LAB_CHECK_LABELS[i], state }));
}

export interface LabResultMessage {
  severity: 'success' | 'info' | 'warning' | 'error';
  text: string;
}

/**
 * What step 4 says, from the job's end (outcome, writeBack) and the recipe status
 * lab.ps1 left behind (#1710). The recipe status alone is ambiguous: `draft` after
 * a failed run means a prereq or the VM failed, `testing` after a finished run
 * means the write-back was skipped (cancelled, vetoed or failed).
 */
export function labResultMessage(input: {
  rootId: string;
  outcome?: string | null;
  writeBack?: string | null;
  recipeStatus: string | null;
  events: LabEvent[];
  failClass?: string | null;
  gaveUp: boolean;
}): LabResultMessage {
  const { rootId, outcome, writeBack, recipeStatus, events, failClass, gaveUp } = input;
  if (gaveUp) {
    return { severity: 'warning', text: 'Der Lab-Job hat sich nicht gemeldet. Die Prüfung kann nach 180 min neu gestartet werden.' };
  }
  if (writeBack?.startsWith('error')) {
    return { severity: 'warning', text: `Das Ergebnis wurde nicht in den Katalog geschrieben (${writeBack}). Bitte die Prüfung neu starten.` };
  }
  if (outcome === 'stopped') {
    return { severity: 'warning', text: 'Die Prüfung wurde abgebrochen; das Ergebnis wurde nicht übernommen. Sie kann nach 180 min neu gestartet werden.' };
  }
  if (recipeStatus === 'verified') return { severity: 'success', text: 'Das Rezept ist geprüft und im Image-Wizard wählbar.' };
  if (recipeStatus === 'failed') return { severity: 'error', text: 'Das Rezept ist bei der Prüfung fehlgeschlagen.' };
  if (recipeStatus === 'draft' && outcome === 'failed') {
    const failure = lastFailure(events, failClass);
    if (failure?.appId !== rootId) {
      const who = failure?.appId ? `die Voraussetzung ${failure.appId}` : 'die Test-VM';
      return { severity: 'info', text: `Nicht bewertet: ${who} ist fehlgeschlagen. Das Rezept bleibt ein Entwurf.` };
    }
  }
  return { severity: 'info', text: 'Das Ergebnis wurde nicht übernommen - das Rezept wurde inzwischen geändert oder neu geprüft.' };
}

/** Phase, fail class and recipe of the failure - from the last per-recipe fail event, else the job. */
export function lastFailure(
  events: LabEvent[],
  jobFailClass: string | null | undefined
): { phase: string; failClass: string; appId?: string } | null {
  const fail = [...events].reverse().find((e) => e.status === 'fail' && e.appId);
  if (fail) return { phase: fail.phase ?? '?', failClass: fail.failClass ?? 'agent-error', appId: fail.appId };
  const any = [...events].reverse().find((e) => e.status === 'fail');
  if (any) return { phase: any.phase ?? '?', failClass: any.failClass ?? jobFailClass ?? 'agent-error' };
  return jobFailClass ? { phase: 'orchestrator', failClass: jobFailClass } : null;
}
